import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton, Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Check, X, Eye, Clock, Search, CheckCircle2, XCircle, AlertCircle, CalendarDays, FilterX, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { tokens } from '@/theme/tokens';
import { PageHeader } from '@/components/navigation/PageHeader';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import useLeaveStore from '@/store/leaveStore';
import { useAuth } from '@/hooks/useAuth';
import { useDepartments, useDesignations } from '@/hooks/useStaffManagement';
import { LeaveTimelineModal } from './components/LeaveTimelineModal';
import { InitializeLeaveModal } from '@/features/staff-management/components/InitializeLeaveModal';
import { ErrorState } from '@/components/feedback/ErrorState';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, borderBottom: `1px solid ${tokens.colors.border}`, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export function LeaveRequestsPage() {
  const { user } = useAuth();
  const userRole = String(user?.role || '').toLowerCase();
  const isPrincipal = userRole === 'principal' || userRole === 'super_admin' || userRole === 'organization_admin';

  const { pendingLeaves, loading, error, fetchPendingLeaves, approveLeave, rejectLeave } = useLeaveStore();
  const { data: departments = [] } = useDepartments();
  const { data: designations = [] } = useDesignations();

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [initModalOpen, setInitModalOpen] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedDesig, setSelectedDesig] = useState('');

  // Approve dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveRemarks, setApproveRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Trigger backend query whenever search, department, or designation filter changes
  useEffect(() => {
    const params = {
      page: 1,
      page_size: 50,
      search: searchTerm || undefined,
      department_id: selectedDept || undefined,
      designation_id: selectedDesig || undefined,
    };
    fetchPendingLeaves(params);
  }, [searchTerm, selectedDept, selectedDesig]);

  // Filter designation options based on selected department
  const filteredDesignations = useMemo(() => {
    if (!selectedDept) return designations || [];
    return (designations || []).filter(
      (d) => d.department_id === selectedDept || d.departmentId === selectedDept
    );
  }, [designations, selectedDept]);

  // Sort pending leaves based on appliedOn field (newest on top)
  const sortedPendingLeaves = useMemo(() => {
    if (!pendingLeaves || !Array.isArray(pendingLeaves)) return [];
    return [...pendingLeaves].sort((a, b) => {
      const timeA = a.appliedOn ? new Date(a.appliedOn).getTime() : 0;
      const timeB = b.appliedOn ? new Date(b.appliedOn).getTime() : 0;
      const validA = isNaN(timeA) ? 0 : timeA;
      const validB = isNaN(timeB) ? 0 : timeB;
      return validB - validA;
    });
  }, [pendingLeaves]);

  // Calculate dynamic stats from backend response
  const stats = useMemo(() => {
    if (!pendingLeaves || !Array.isArray(pendingLeaves)) {
      return { pending: 0, approvedToday: 0, rejectedToday: 0, total: 0 };
    }
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    let pending = 0;
    let approvedToday = 0;
    let rejectedToday = 0;

    pendingLeaves.forEach((item) => {
      const status = String(item.status || '').toUpperCase();
      const appliedDateStr = item.appliedOn ? String(item.appliedOn).split('T')[0] : '';
      const updatedDateStr = item.updatedAt ? String(item.updatedAt).split('T')[0] : '';
      const approvedDateStr = item.approvedOn ? String(item.approvedOn).split('T')[0] : '';
      const rejectedDateStr = item.rejectedOn ? String(item.rejectedOn).split('T')[0] : '';

      if (status === 'PENDING') {
        pending += 1;
      } else if (status === 'APPROVED') {
        const isToday =
          approvedDateStr === todayStr ||
          appliedDateStr === todayStr ||
          updatedDateStr === todayStr ||
          !appliedDateStr;
        if (isToday) {
          approvedToday += 1;
        }
      } else if (status === 'REJECTED') {
        const isToday =
          rejectedDateStr === todayStr ||
          appliedDateStr === todayStr ||
          updatedDateStr === todayStr ||
          !appliedDateStr;
        if (isToday) {
          rejectedToday += 1;
        }
      }
    });

    return {
      pending,
      approvedToday,
      rejectedToday,
      total: pendingLeaves.length,
    };
  }, [pendingLeaves]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return format(d, 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return format(d, 'dd MMM yyyy, hh:mm a');
    } catch {
      return dateStr;
    }
  };

  const handleOpenApprove = (leave) => {
    setSelectedLeave(leave);
    setApproveRemarks('');
    setApproveDialogOpen(true);
  };

  const handleOpenReject = (leave) => {
    setSelectedLeave(leave);
    setRejectionReason('');
    setRejectRemarks('');
    setRejectDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedLeave) return;
    setActionLoading(true);
    try {
      await approveLeave(selectedLeave.id, approveRemarks);
      toast.success('Leave request approved successfully');
      setApproveDialogOpen(false);
      fetchPendingLeaves({
        search: searchTerm || undefined,
        department_id: selectedDept || undefined,
        designation_id: selectedDesig || undefined,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to approve request';
      toast.error(typeof msg === 'string' ? msg : 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!selectedLeave) return;
    setActionLoading(true);
    try {
      await rejectLeave(selectedLeave.id, rejectRemarks || rejectionReason, rejectionReason);
      toast.success('Leave request rejected');
      setRejectDialogOpen(false);
      fetchPendingLeaves({
        search: searchTerm || undefined,
        department_id: selectedDept || undefined,
        designation_id: selectedDesig || undefined,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to reject request';
      toast.error(typeof msg === 'string' ? msg : 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenTimeline = (leaveData) => {
    setSelectedLeave(leaveData);
    setTimelineModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedDept('');
    setSelectedDesig('');
  };

  const getStatusChip = (status) => {
    const s = String(status || '').toUpperCase();
    switch (s) {
      case 'APPROVED':
        return <Chip label="Approved" color="success" size="small" icon={<CheckCircle2 size={14} />} />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" icon={<XCircle size={14} />} />;
      case 'CANCELLED':
        return <Chip label="Cancelled" color="default" size="small" icon={<AlertCircle size={14} />} />;
      case 'PENDING':
      default:
        return <Chip label="Pending" color="warning" size="small" icon={<Clock size={14} />} />;
    }
  };

  const columns = [
    {
      field: 'staffName',
      headerName: 'Staff Member',
      flex: 1.1,
      minWidth: 130,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {params.row.staffName || 'Staff Member'}
          </Typography>
          {params.row.employeeId && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
              ID: {params.row.employeeId}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'leaveType',
      headerName: 'Leave Type',
      flex: 0.9,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'fromDate',
      headerName: 'From Date',
      flex: 0.8,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'toDate',
      headerName: 'To Date',
      flex: 0.8,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'numberOfDays',
      headerName: 'Days',
      flex: 0.5,
      minWidth: 50,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1.1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const text = params.value || params.row?.reason || '-';
        return (
          <Tooltip title={text} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                cursor: 'pointer',
              }}
            >
              {text}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'appliedOn',
      headerName: 'Applied On',
      flex: 1,
      minWidth: 115,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.4,
      minWidth: 165,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const isPending = String(params.row.status || '').toUpperCase() === 'PENDING';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenTimeline(params.row)}>
                <Eye size={16} />
              </IconButton>
            </Tooltip>

            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<Check size={14} />}
              onClick={() => handleOpenApprove(params.row)}
              disabled={!isPending}
              sx={{ px: 1, py: 0.25, fontSize: '0.75rem', minWidth: 60, whiteSpace: 'nowrap' }}
            >
              Approve
            </Button>

            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<X size={14} />}
              onClick={() => handleOpenReject(params.row)}
              disabled={!isPending}
              sx={{ px: 1, py: 0.25, fontSize: '0.75rem', minWidth: 55, whiteSpace: 'nowrap' }}
            >
              Reject
            </Button>
          </Box>
        );
      },
    },
  ];

  const hasActiveFilters = Boolean(searchTerm || selectedDept || selectedDesig);

  if (error) {
    return (
      <Box sx={{ pb: 4 }}>
        <PageHeader
          title="Leave Requests Approval"
          subtitle="Review, approve, or reject incoming staff leave applications."
        />
        <ErrorState error={error} onRetry={() => fetchPendingLeaves()} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Leave Requests Approval"
        subtitle="Review, approve, or reject incoming staff leave applications."
        action={
          isPrincipal ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Sparkles size={18} />}
              onClick={() => setInitModalOpen(true)}
            >
              Initialize Leave Balances
            </Button>
          ) : null
        }
      />

      {/* 4 Stat Cards in 1 Row */}
      <DashboardGrid columns={{ xs: 1, sm: 2, md: 4 }}>
        <StatCard
          title="Pending Requests"
          value={stats.pending}
          icon={Clock}
          color="warning"
        />
        <StatCard
          title="Approved Today"
          value={stats.approvedToday}
          icon={CheckCircle2}
          color="success"
        />
        <StatCard
          title="Rejected Today"
          value={stats.rejectedToday}
          icon={XCircle}
          color="error"
        />
        <StatCard
          title="Total Applications"
          value={stats.total}
          icon={CalendarDays}
          color="info"
        />
      </DashboardGrid>

      {/* Enterprise DataGrid Wrapped in WidgetContainer */}
      <WidgetContainer noPadding>
        {/* Filter Bar with Search, Department, and Designation */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by staff name, employee ID, or leave type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDept}
              label="Department"
              onChange={(e) => {
                setSelectedDept(e.target.value);
                setSelectedDesig('');
              }}
            >
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {(departments || []).map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Designation</InputLabel>
            <Select
              value={selectedDesig}
              label="Designation"
              onChange={(e) => setSelectedDesig(e.target.value)}
            >
              <MenuItem value=""><em>All Designations</em></MenuItem>
              {filteredDesignations.map((desig) => (
                <MenuItem key={desig.id} value={desig.id}>
                  {desig.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button
              variant="outlined"
              startIcon={<FilterX size={18} />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={sortedPendingLeaves}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id || Math.random().toString()}
            pageSizeOptions={[10, 20, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: {
                sortModel: [{ field: 'appliedOn', sort: 'desc' }],
              },
            }}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
            }}
          />
        </Box>
      </WidgetContainer>

      {/* Approve Modal */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Leave Request</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to approve leave for <strong>{selectedLeave?.staffName}</strong>?
          </Typography>
          <TextField
            label="Remarks (Optional)"
            multiline
            rows={2}
            value={approveRemarks}
            onChange={(e) => setApproveRemarks(e.target.value)}
            placeholder="Add approval note..."
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirmApprove} disabled={actionLoading}>
            {actionLoading ? 'Approving...' : 'Confirm Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reject Leave Request</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            Rejecting leave for <strong>{selectedLeave?.staffName}</strong>:
          </Typography>

          <TextField
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Specify reason for rejection"
            required
            fullWidth
          />

          <TextField
            label="Remarks (Optional)"
            multiline
            rows={2}
            value={rejectRemarks}
            onChange={(e) => setRejectRemarks(e.target.value)}
            placeholder="Additional notes"
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmReject} disabled={actionLoading}>
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline / Detail Modal */}
      <LeaveTimelineModal
        open={timelineModalOpen}
        onClose={() => setTimelineModalOpen(false)}
        leaveData={selectedLeave}
      />

      {/* Initialize Leave Balances Modal */}
      <InitializeLeaveModal
        open={initModalOpen}
        onClose={() => setInitModalOpen(false)}
        onSuccess={() => fetchPendingLeaves()}
      />
    </Box>
  );
}
